import React, { forwardRef, ForwardRefRenderFunction, useEffect, useImperativeHandle, useState } from 'react'
import { Field } from 'react-final-form'

import { FieldState, FormApi } from 'final-form'
import { cloneDeep, get, isEmpty, mapValues, merge, set } from 'lodash-es'

import { Contentmetadata } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import {
  FieldSchemaDefinition,
  FieldValue,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import SecondaryButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/secondaryButton/SecondaryButton'
import DataTable from '@lyrahealth-inc/ui-core/lib/molecules/table/DataTable'
import { CheckboxRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'

import styles from './configureCheckIn.module.scss'

interface FieldMetadata extends FieldSchemaDefinition<any> {
  isRequired?: boolean
}

type ConfigureCheckInProps = {
  formValues: any
  metadata: Contentmetadata
  name?: string
  initialize: (formValues: any) => void
  form: FormApi
}

const ConfigureCheckIn: ForwardRefRenderFunction<ConfigureCheckInRef, ConfigureCheckInProps> = (
  { formValues, metadata, name = 'checkInConfig', initialize, form },
  ref,
) => {
  const [addingCustomField, setAddingCustomField] = useState(false)
  const [customFieldType, setCustomFieldType] = useState<string>('')
  const [customFieldTitle, setCustomFieldTitle] = useState<string>()
  const [customFieldsIndex, setCustomFieldsIndex] = useState(0)
  const [customFields, setCustomFields] = useState<Record<string, { schema: any; uiSchema: any }>>({})
  const [customFieldMaximum, setCustomFieldMaximum] = useState<number>()

  const [maxFieldsReached, setMaxFieldsReached] = useState(false)

  const [columnarTableData, setColumnarTableData] = useState<Dict[]>([])

  useImperativeHandle(ref, () => ({
    configure(values) {
      return configure(values)
    },
  }))

  useEffect(() => {
    // Initialize columnar table data from metadata
    const fieldsMeta = metadata?.schema?.properties || {}
    const columnarData = []

    const sortedTableData = Object.values(fieldsMeta).sort((a: FieldMetadata, b: FieldMetadata) => {
      if (a.isRequired && !b.isRequired) return -1
      if ((a.isRequired && b.isRequired) || (!a.isRequired && !b.isRequired)) return a.show ? -1 : 1
      return 1
    })

    const sortedTableDataWithCustomFields = sortedTableData.concat(
      Object.keys(customFields).map((fieldKey) => {
        return { ...customFields[fieldKey].schema }
      }),
    )

    const numRows = Math.ceil(sortedTableDataWithCustomFields.length / 3)
    for (let row = 0; row < numRows; row++) {
      columnarData.push({
        col1: sortedTableDataWithCustomFields[row] || null,
        col2: sortedTableDataWithCustomFields[row + numRows] || null,
        col3: sortedTableDataWithCustomFields[row + numRows * 2] || null,
      })
    }

    setColumnarTableData(columnarData)
  }, [metadata, customFields])

  const getCheckInCell = (row: any, columnKey: string) => {
    if (row.original[columnKey] === null) {
      return null
    }

    const disabled =
      row.original[columnKey].isRequired ||
      (maxFieldsReached && !get(formValues, `${name}.${row.original[columnKey].name}`))
    return (
      <div className={styles['row-wrapper']}>
        <Field
          name={`${name}.${row.original[columnKey].name}`}
          component={CheckboxRFF}
          disabled={disabled}
          title={row.original[columnKey].title}
          baseInputStyle={{ marginBottom: 0 }}
        />
        {row.original[columnKey].custom ? (
          <button
            type='button'
            aria-label={`Remove ${row.original[columnKey].title}`}
            className={styles['remove-custom-field']}
            onClick={(event: React.MouseEvent<HTMLElement>) => removeCustomField(event, row.original[columnKey])}
          >
            <XIcon size={16} fillColor={styles.x_light_gray_100} />
          </button>
        ) : (
          ''
        )}
      </div>
    )
  }

  const tableColumns = [
    {
      Header: '',
      accessor: (row: any) => row?.col1?.title || '',
      Cell: (row: any) => getCheckInCell(row, 'col1'),
      id: 'col1',
    },
    {
      Header: '',
      accessor: (row: any) => row?.col2?.title || '',
      Cell: (row: any) => getCheckInCell(row, 'col2'),
      id: 'col2',
    },
    {
      Header: '',
      accessor: (row: any) => row?.col3?.title || '',
      Cell: (row: any) => getCheckInCell(row, 'col3'),
      id: 'col3',
    },
  ]

  useEffect(() => {
    const initialValues = formValues ? formValues : {}
    const assignmentValues: Record<string, boolean> = {}
    const fieldsMeta = metadata?.schema?.properties || {}
    // Formats data to:    property: true/false
    Object.entries(fieldsMeta).forEach(([key, value]) => {
      assignmentValues[key] = (value as any).show ?? true
    })

    initialValues[name] = assignmentValues
    initialize(initialValues)

    const customFieldsIndex = getCustomFieldsCount(fieldsMeta)
    setCustomFieldsIndex(customFieldsIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const configure = (values: Dict) => {
    // We first start by grabbing the 'properties' of the fields. Such as, min value and max value.
    const metaDataSchemaProperties = get(metadata, 'schema.properties')
    let fieldsMetaData = mapValues(cloneDeep(metaDataSchemaProperties), (fieldMeta: any, fieldName: string) => {
      // We want to be really sure we are showing the ones that we want to show by default
      fieldMeta = set(fieldMeta, 'show', get(values, `${name}.${fieldName}`, false))
      return fieldMeta
    })

    // We'll want to maintain the original UI widgets if there are any present in the meta_data
    let metadataUISchema = get(metadata, 'uiSchema')
    // Here is the check for if there are custom fields present.
    if (!isEmpty(customFields)) {
      // We first begin by 'merging' or adding the custom field JSON's key and value to the end of our array
      // containing all of the 'properties' of our fields.
      fieldsMetaData = merge(
        fieldsMetaData,
        mapValues(customFields, (customField: any) => get(customField, 'schema')),
      )

      // Then we add the widgets associated with the custom fields into our separate widget array.
      metadataUISchema = merge(
        cloneDeep(metadataUISchema),
        mapValues(customFields, (customField: any) => get(customField, 'uiSchema')),
      )
    }
    // Now we update the entire metadata of our widgets with the proper widgets/uiSchema, including the custom fields(if there are any).
    metadata = set(cloneDeep(metadata), 'uiSchema', metadataUISchema)
    // Ensure every field that is shown has some default value. Integers should have a default of 0.
    metadata = set(
      metadata,
      'initialValues',
      mapValues(fieldsMetaData, (fieldMeta: any) => (get(fieldMeta, 'type') === 'integer' ? 0 : undefined)),
    )
    // Update the display layer once again to the client, but this time, the properties.
    metadata = set(metadata, 'schema.properties', fieldsMetaData)

    return metadata
  }

  const getCustomFieldsCount = (meta: any) => {
    if (!meta) return 0
    let counter = 0
    for (const key in meta) {
      if (meta[key].custom) {
        counter++
      }
    }
    return counter
  }

  const removeCustomField = (event: any, field: any) => {
    event.preventDefault()
    const newCustomFields = { ...customFields }

    delete newCustomFields[field.name]

    // Remove the custom field from all the metadata if it was added by config()
    if (metadata?.schema?.properties?.[field.name]) {
      delete metadata.schema.properties[field.name]
    }

    if (metadata?.uiSchema?.[field.name]) {
      delete metadata.uiSchema[field.name]
    }

    if (metadata?.initialValues?.[field.name]) {
      delete metadata.initialValues[field.name]
    }

    const selectedFields = get(formValues, name)
    Object.keys(selectedFields).forEach((key) => {
      if (!selectedFields[key]) delete selectedFields[key]
    })

    setCustomFields(newCustomFields)
    setMaxFieldsReached(!(Object.keys(selectedFields).length <= 99))
  }

  const configureCustomField = () => {
    return (
      <div className={styles['add-custom-field-row']}>
        <div>
          <label className={styles['field-label']} htmlFor='custom-field-type'>
            Field Type
          </label>
          <select name='custom-field-type' onChange={(e) => setCustomFieldType(e.target.value)} id='custom-field-type'>
            <option value='' hidden>
              Select...
            </option>
            <option value='string'>Free text</option>
            <option value='integer'>Number</option>
            <option value='boolean'>Yes/No</option>
          </select>
        </div>
        {customFieldType === 'integer' ? (
          <div>
            <label className={styles['field-label']} htmlFor='custom-field-max-value'>
              Maximum
            </label>
            <input
              id='custom-field-max-value'
              type='number'
              min='0'
              value={customFieldMaximum || '10'}
              onChange={(e) => setCustomFieldMaximum(parseInt(e.target.value, 10))}
            />
          </div>
        ) : (
          ''
        )}
        <div style={{ flexBasis: '40%' }}>
          <label className={styles['field-label']} htmlFor='custom-field-diplayed-label'>
            Displayed Label
          </label>
          <input
            id='custom-field-diplayed-label'
            data-test-id='ConfigureCheckIn-displayed-label'
            type='text'
            onChange={(e) => setCustomFieldTitle(e.target.value)}
            style={{ width: '90%' }}
            disabled={!customFieldType}
          />
        </div>
        <SecondaryButton
          data-test-id='ConfigureCheckIn-save-button'
          onClick={createCustomField}
          disabled={!customFieldTitle || maxFieldsReached}
        >
          Save
        </SecondaryButton>
      </div>
    )
  }

  const createCustomFieldName = () => {
    const currentIndex = customFieldsIndex
    return `checkIn${currentIndex}`
  }

  const createCustomField = () => {
    const customField = {}
    const customName = createCustomFieldName()

    switch (customFieldType) {
      case 'string':
        ;(customField as any).schema = {
          custom: true,
          name: customName,
          show: true,
          title: customFieldTitle,
          type: 'string',
        }
        ;(customField as any).uiSchema = {
          'ui:widget': 'textarea',
        }
        break
      case 'boolean':
        ;(customField as any).schema = {
          custom: true,
          name: customName,
          show: true,
          title: customFieldTitle,
          type: 'boolean',
        }
        ;(customField as any).uiSchema = {
          'ui:widget': 'radio',
        }
        break
      case 'integer': {
        const maximum = customFieldMaximum || 10
        ;(customField as any).schema = {
          custom: true,
          default: 0,
          maximum,
          minimum: 0,
          name: customName,
          show: true,
          title: customFieldTitle,
          type: 'integer',
        }
        ;(customField as any).uiSchema = {
          'ui:widget': 'range',
        }
        break
      }
    }

    const newCustomFields = set({ ...customFields }, customName, customField)

    setCustomFields(newCustomFields)
    setAddingCustomField(false)
    setCustomFieldType('')
    setCustomFieldMaximum(10)
    setCustomFieldTitle('')
    setCustomFieldsIndex(customFieldsIndex + 1)

    // Set the intitial formValue for the custom field to true (checked) so it matches the 'show' schema value
    set(formValues, `${name}.${customName}`, true)

    // Register the custom field and subscribe to changes in the value
    form.registerField(
      `${name}.${customName}`,
      (fieldState: FieldState<FieldValue>) => {
        const { value, change } = fieldState
        // Update the formValues with the value of the checkbox
        change(value)
      },
      { value: true },
    )

    const selectedFields = get(formValues, name)
    Object.keys(selectedFields).forEach((key) => {
      if (!selectedFields[key]) delete selectedFields[key]
    })

    // 99 is an arbitrary number to represent no limit for allowed selections.
    setMaxFieldsReached(Object.keys(selectedFields).length >= 99)
  }

  const addCustomDisabled = Object.keys(customFields).length >= 99 || maxFieldsReached || addingCustomField
  return (
    <div className={styles.content}>
      <div className={styles['tables-container']}>
        <DataTable
          data={columnarTableData}
          columns={tableColumns}
          pageSize={columnarTableData.length}
          sortable={false}
          resizable={false}
          pagination={false}
        />
      </div>
      {addingCustomField ? configureCustomField() : ''}
      <SecondaryButton
        customClass={styles.customFieldButton}
        disabled={addCustomDisabled}
        onClick={() => setAddingCustomField(true)}
      >
        + Add custom field
      </SecondaryButton>
    </div>
  )
}

export type ConfigureCheckInRef = {
  configure: (values: Dict) => any
}

export default forwardRef(ConfigureCheckIn)
